import { isNil, uniq } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil';
import type { AnalysisView, OperationError, OperationResult, PrivatePortfolioNode } from 'venn-api';
import { getSpecificAnalysisView, viewEntity } from 'venn-api';
import type { AfterUnsavedChangeAction } from 'venn-components';
import { StudioPrintSettingsContext, UserContext } from 'venn-components';
import {
  allocatorAnalysisSubject,
  analysisViewIdState,
  proxySetIdState,
  currentAnalysisView,
  hasUnsavedChangesState,
  openPrivateAllocatorPortfolio,
  originalAnalysisSubjectQuery,
  useRecoilValueWithDefault,
  useSendSignal,
  useSyncBaseline,
  workspaceFeeLimitsState,
} from 'venn-state';
import { Notifications, NotificationType } from 'venn-ui-kit';
import {
  type CustomizableBlockSetting,
  isViewTemplate,
  type StudioTemplateState,
  AnalysisSubject,
  getViewIdFromLocation,
  logExceptionIntoSentry,
  parseValueFromLocation,
  Routes,
  updateUrlParam,
  assertNotNil,
  convertTemplateToView,
  useHasFF,
} from 'venn-utils';

import { applyDefaultFeesToSubjectGroups, trackViewOpened } from './studioUtils';
import { useSyncAllocatorConfigToView } from './useSyncAllocatorConfigToView';
import { useSyncPrivateAllocatorConfigToView } from './useSyncPrivateAllocatorConfigToView';

/** Handle Studio setup information, such as fetching new view, store view into localstorage and sync url */
const useStudioSetup = () => {
  const { hasPermission } = useContext(UserContext);
  const { loadSettings: loadStudioPrintSettings } = useContext(StudioPrintSettingsContext);
  const isFetchingNewViewRef = useRef(false);
  const [draggingBlock, setDraggingBlock] = useState<CustomizableBlockSetting>();
  const history = useHistory();
  const location = useLocation<StudioTemplateState>();
  const locationSavedId = parseValueFromLocation(location, 'savedId');
  const hasUnsavedChanges = useRecoilValueWithDefault(hasUnsavedChangesState, false);
  const hasDefaultRLAdvisoryFeesFF = useHasFF('default_rl_advisory_fees_ff');
  const workspaceFeeLimits = useRecoilValue(workspaceFeeLimitsState);
  useSyncBaseline();

  const setPortfolioInPrivateAllocator = useRecoilCallback(
    ({ snapshot, set }) =>
      async (portfolio?: PrivatePortfolioNode, unsavedModifiedPortfolio?: PrivatePortfolioNode) => {
        const studioSubject = { privatePortfolioId: portfolio?.id };
        const proxySetId = await snapshot.getPromise(proxySetIdState);

        // portfolio that is passed into this function is coming from history.state and can be stale
        const portfolioFromServer = (await snapshot.getPromise(originalAnalysisSubjectQuery(studioSubject)))
          ?.privatePortfolio;

        if (isNil(portfolioFromServer)) {
          return;
        }

        set(openPrivateAllocatorPortfolio, portfolioFromServer);
        set(
          allocatorAnalysisSubject(studioSubject),
          new AnalysisSubject(unsavedModifiedPortfolio ?? portfolioFromServer, 'private-portfolio', proxySetId),
        );
      },
    [],
  );

  const [firstOpeningOfTheView, setFirstOpeningOfTheView] = useState(false);
  const [isGlobalAnalysisRangeLoading, setIsGlobalAnalysisRangeLoading] = useState(false);
  const [isViewCurrentlyLoaded, setIsViewCurrentlyLoaded] = useState(false);
  const setCurrentAnalysisView = useSetRecoilState(currentAnalysisView);
  const currentViewId = useRecoilValue(analysisViewIdState);

  const [afterUnsavedChangesAction, setAfterUnsavedChangesAction] = useState<AfterUnsavedChangeAction>();

  const canCreateTemplate = useMemo(() => hasPermission('STUDIO_CREATE_TEMPLATE'), [hasPermission]);

  const [isCheckingDuplicateReportName, setIsCheckingDuplicateReportName] = useState(false);
  const [isDuplicateReportName, setIsDuplicateReportName] = useState(false);

  const resetState = useSendSignal({ type: 'StudioReset' });

  useSyncAllocatorConfigToView();
  useSyncPrivateAllocatorConfigToView();

  const fetchNewView = useCallback(
    async (viewId?: string, localAnalysisView?: AnalysisView) => {
      // Prevent fetch new view while there is another one in progress
      if (isFetchingNewViewRef.current) {
        return;
      }

      isFetchingNewViewRef.current = true;

      resetState();
      const editTemplate = parseValueFromLocation(history.location, 'editTemplate') === 'true';
      const isReport = history.location.pathname.startsWith(Routes.REPORT_LAB_PATH);

      setIsGlobalAnalysisRangeLoading(true);

      setPortfolioInPrivateAllocator(undefined);
      setIsDuplicateReportName(false);
      setIsCheckingDuplicateReportName(false);
      loadStudioPrintSettings();

      if (!canCreateTemplate && editTemplate) {
        isFetchingNewViewRef.current = false;
        setIsGlobalAnalysisRangeLoading(false);
        return;
      }

      let view: AnalysisView | undefined = localAnalysisView;

      try {
        if (viewId) {
          const { content: savedView } = await getSpecificAnalysisView(viewId);
          view = savedView;
          trackViewOpened(view);
        }

        const uniqueSubjectIds = uniq(view?.subjects.map((s) => s.id) ?? []);

        const template = isViewTemplate(view?.analysisViewType);
        if (view) {
          if (template && !editTemplate) {
            view = convertTemplateToView(view);
            applyDefaultFeesToSubjectGroups(view.subjectGroups, workspaceFeeLimits, hasDefaultRLAdvisoryFeesFF);
            updateUrlParam(history, 'REPLACE', 'savedId', undefined);
            // Auto open setup modal when it's a template but not in edit mode
            setFirstOpeningOfTheView(true);
          } else if (!template && viewId) {
            // when loading a pre-existing view that hasn't saved fees default subjects to 0% fees for legacy reports
            applyDefaultFeesToSubjectGroups(view.subjectGroups, workspaceFeeLimits, hasDefaultRLAdvisoryFeesFF, 0);
          }
        }

        !editTemplate && updateUrlParam(history, 'REPLACE', 'editTemplate', undefined);

        setCurrentAnalysisView(assertNotNil(view));
        Promise.all(
          uniqueSubjectIds.map((subjectId) =>
            viewEntity(
              subjectId.toString(),
              viewId
                ? {
                    viewId,
                    viewType: view?.analysisViewType,
                  }
                : undefined,
            ),
          ),
        );
      } catch (e) {
        const { status } = e as OperationResult<OperationError>;
        if (status === 403) {
          const params = new URLSearchParams({
            noPermissions: 'true',
          });
          history.replace(`${Routes.HOME_PATH}?${params.toString()}`);
          return;
        }
        Notifications.notify(`Unable to load ${isReport ? 'report' : 'studio view'}`, NotificationType.ERROR);
        // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
        logExceptionIntoSentry(e);
        history.push(Routes.HOME_PATH);
      } finally {
        isFetchingNewViewRef.current = false;
        setIsGlobalAnalysisRangeLoading(false);
        setIsViewCurrentlyLoaded(true);
      }
    },
    [
      resetState,
      history,
      setPortfolioInPrivateAllocator,
      loadStudioPrintSettings,
      canCreateTemplate,
      setCurrentAnalysisView,
      hasDefaultRLAdvisoryFeesFF,
      workspaceFeeLimits,
    ],
  );

  const checkHasUnsavedState = useCallback(
    (proceedCallback: () => void) => {
      if (isFetchingNewViewRef.current) {
        return;
      }

      if (hasUnsavedChanges && isViewCurrentlyLoaded) {
        // Un applied change would prompt warning modal
        setAfterUnsavedChangesAction({
          proceedCallback,
          cancelCallback: undefined,
          navigateToNew: true,
        });
      } else {
        proceedCallback();
      }
    },
    [hasUnsavedChanges, isViewCurrentlyLoaded],
  );

  const handleHistoryStateUpdate = useCallback(
    (id: string | undefined, newDocument: AnalysisView) => {
      checkHasUnsavedState(() => {
        fetchNewView(id, newDocument);
      });
    },
    [checkHasUnsavedState, fetchNewView],
  );

  useEffect(() => {
    if (!location.state) {
      return;
    }

    const newDocument = location.state.newDocument;
    const newDocumentId = location.state.id || locationSavedId;
    if (newDocument) {
      handleHistoryStateUpdate(newDocumentId, newDocument);
    }
    const openPrivatePortfolioAllocator = location.state.openAllocatorForPrivatePortfolio;
    const modifiedUnsavedPortfolio = location.state.modifiedUnsavedPortfolio;
    if (!isNil(openPrivatePortfolioAllocator)) {
      setPortfolioInPrivateAllocator(openPrivatePortfolioAllocator, modifiedUnsavedPortfolio);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    const newDocument = location.state?.newDocument;
    if (newDocument) {
      return;
    }
    const viewIdFromLocation = getViewIdFromLocation(history.location);
    if (viewIdFromLocation && currentViewId !== viewIdFromLocation) {
      checkHasUnsavedState(() => {
        fetchNewView(viewIdFromLocation);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Reset all view state when studio / report lab dismounts
  useEffect(
    () => resetState,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset state on dismount
    [],
  );

  const studioSetupValue = useMemo(
    () => ({
      isFetchingNewViewRef,
      draggingBlock,
      setDraggingBlock,
      isGlobalAnalysisRangeLoading,
      setIsGlobalAnalysisRangeLoading,
      firstOpeningOfTheView,
      setFirstOpeningOfTheView,
      afterUnsavedChangesAction,
      setAfterUnsavedChangesAction,
      isDuplicateReportName,
      setIsDuplicateReportName,
      isCheckingDuplicateReportName,
      setIsCheckingDuplicateReportName,
    }),
    [
      afterUnsavedChangesAction,
      draggingBlock,
      firstOpeningOfTheView,
      isCheckingDuplicateReportName,
      isDuplicateReportName,
      isGlobalAnalysisRangeLoading,
    ],
  );
  return studioSetupValue;
};

export default useStudioSetup;
